import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, Navigation, Truck } from "lucide-react";
import Seo, { localBusinessJsonLd, breadcrumbJsonLd } from "@/components/Seo";
import { Breadcrumbs, StarRating, ProductCard } from "@/components/common";
import { SITE, PRODUCTS, REVIEWS } from "@/mock";

export default function StoreDetail() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Location", path: "/locations" },
    { name: "Fort Lauderdale", path: "/locations/fort-lauderdale" },
  ];
  const featured = PRODUCTS.filter((p) => p.category === "mattresses").slice(0, 4);
  return (
    <>
      <Seo
        title="Fort Lauderdale Mattress Store - 2677 N Federal Hwy"
        description="MattressTown Fort Lauderdale at 2677 N Federal Hwy. Shop Beautyrest Black, Puffy & Serta with same-day delivery, set-up & removal. Open 7 days a week."
        path="/locations/fort-lauderdale"
        image={SITE.storePhotos[1]}
        jsonLd={[localBusinessJsonLd(), breadcrumbJsonLd(crumbs)]}
      />
      <Breadcrumbs items={crumbs} />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-2">
          <div>
            <h1 className="text-4xl font-bold uppercase leading-tight text-neutral-900">MattressTown Fort Lauderdale</h1>
            <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-neutral-700"><StarRating value={5} size={16} /> {SITE.rating.toFixed(1)} · {SITE.reviewCount} Google Reviews</p>
            <p className="mt-5 leading-relaxed text-neutral-600">
              Our Fort Lauderdale showroom on N Federal Highway is your local
              destination for premium mattresses at honest prices. As a
              family-owned store, we take pride in personal service - come try our
              full lineup of Beautyrest Black, Puffy and Serta, and enjoy free
              same-day delivery, professional set-up and old-mattress removal.
            </p>
            <address className="mt-6 space-y-3 text-sm not-italic text-neutral-700">
              <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 text-[#D0021B]" aria-hidden="true" /> {SITE.address.full}</p>
              <a href={SITE.phoneHref} className="flex items-center gap-2 hover:text-[#D0021B]"><Phone className="h-4 w-4 text-[#D0021B]" aria-hidden="true" /> {SITE.phone}</a>
              <div className="flex items-start gap-2"><Clock className="mt-0.5 h-4 w-4 text-[#D0021B]" aria-hidden="true" /><ul>{SITE.hours.map((h) => (<li key={h.day}>{h.day}: {h.time}</li>))}</ul></div>
              <p className="flex items-center gap-2"><Truck className="h-4 w-4 text-[#D0021B]" aria-hidden="true" /> Same-day delivery across Miami-Dade, Broward &amp; Palm Beach counties</p>
            </address>
            <a href={SITE.address.mapsUrl} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-7 py-3 text-sm font-bold uppercase tracking-wide text-white hover:bg-[#a80016]"><Navigation className="h-4 w-4" aria-hidden="true" /> Get Directions</a>
          </div>
          <div className="space-y-4">
            <img src={SITE.storePhotos[1]} alt="MattressTown Fort Lauderdale storefront exterior" className="w-full rounded-2xl object-cover" />
            <iframe title="Map" src="https://www.google.com/maps?q=2677+N+Federal+Hwy,+Fort+Lauderdale,+FL+33306&output=embed" className="h-64 w-full rounded-2xl" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>

        <section className="mt-14">
          <div className="flex items-end justify-between">
            <h2 className="text-2xl font-bold uppercase text-neutral-900">Popular at This Store</h2>
            <Link to="/mattresses" className="text-sm font-bold uppercase text-[#D0021B] hover:underline">Shop All →</Link>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-5 md:grid-cols-4">{featured.map((p) => (<ProductCard key={p.slug} product={p} />))}</div>
        </section>

        <section className="mt-14 rounded-2xl bg-cream p-8">
          <h2 className="text-2xl font-bold uppercase text-neutral-900">Recent Customer Reviews</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-3">
            {REVIEWS.slice(0, 3).map((r) => (
              <figure key={r.name} className="rounded-xl border border-neutral-200 bg-white p-5">
                <StarRating value={r.rating} />
                <blockquote className="mt-2 text-sm text-neutral-700">“{r.text}”</blockquote>
                <figcaption className="mt-3 text-sm font-bold text-neutral-900">{r.name}</figcaption>
              </figure>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
